import { css } from '@emotion/react'
import { Box, Button, Link, Menu, MenuItem, useTheme } from '@mui/material'
import React, { ReactElement, useCallback, useEffect } from 'react'
import { useMQ } from '../hooks/useMQ'
import { CONFIG } from '../utils/config'

export function LaunchApp(): ReactElement | null {
  const theme = useTheme()
  const { isBelowXsSize } = useMQ()
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  useEffect(() => {
    const handleScroll = () => {
      if (open) {
        setAnchorEl(null)
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [open])

  const menus = [
    {
      title: 'Huma',
      subTitle: 'Permissionless',
      isNew: true,
      link: CONFIG.permissionlessLink,
    },
    {
      title: 'Huma Institutional',
      subTitle: 'Permissioned',
      isNew: false,
      link: CONFIG.institutionalLink,
    },
  ]

  const onClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const styles = {
    switchButton: css`
      margin-right: ${theme.spacing(1)};
      color: #eae6f0;
      font-size: 1rem;
    `,
    menu: css`
      & .MuiPaper-root {
        border-radius: 16px;
        background: #1f1f1f;
        padding: 8px;
        margin-top: 6px;
        width: 159px;
        > ul {
          padding: 0;
        }
      }
    `,
    menuItem: css`
      color: #eae6f0;
      font-size: 1rem;
      padding: 8px 16px;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      border-radius: 8px;
      background: #1f1f1f;
      transition: background 0.3s ease;
      :hover {
        background: #2f2f2f;
      }
      & a {
        text-decoration: none;
      }
    `,
    menuItemTitle: css`
      color: #fff;
      font-size: 14px;
      font-style: normal;
      font-weight: 700;
      line-height: 22px;
      letter-spacing: 0.1px;
      display: flex;
      align-items: flex-start;
      gap: 4px;
    `,
    menuItemSubTitle: css`
      color: #848484;
      font-size: 14px;
      font-style: normal;
      font-weight: 500;
      line-height: 20px;
      letter-spacing: 0.17px;
    `,
    newTag: css`
      color: #32f490;
      margin-top: -3px;
      font-size: 8px;
      font-style: normal;
      font-weight: 400;
      padding: 0px 6px;
      line-height: 14px;
      letter-spacing: 0.4px;
      border-radius: 32px;
      border: 1px solid #32f490;
      box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.08);
    `,
    button: css`
      padding: ${isBelowXsSize ? '8px 18px' : '8.5px 35px'};
      background: linear-gradient(45deg, #74deff 0%, #c733ff 100%);
      font-weight: 700;

      &:hover {
        box-shadow: inset 0px 0px 28px #ffffff;
        background: linear-gradient(45deg, #74deff 0%, #c733ff 100%);
      }
    `,
  }

  return (
    <Box>
      <Menu
        css={styles.menu}
        anchorEl={anchorEl}
        open={open}
        onClose={() => {
          setAnchorEl(null)
        }}
        disableScrollLock={true}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {menus.map((menu) => (
          <MenuItem 
            css={styles.menuItem} 
            key={menu.title} 
            role='none'
            onClick={() => {
              setAnchorEl(null)
            }}
          >
            <Link
              href={menu.link}
              target='_blank'
              rel='noreferrer'
              role='menuitem'
            >
              <Box css={styles.menuItemTitle}>
                <Box>{menu.title}</Box>
                {menu.isNew && <Box css={styles.newTag}>NEW</Box>}
              </Box>
              <Box css={styles.menuItemSubTitle}>{menu.subTitle}</Box>
            </Link>
          </MenuItem>
        ))}
      </Menu>
      <Button variant='contained' onClick={onClick} css={styles.button}>
        Launch App
      </Button>
    </Box>
  )
}
